import { REVIEWBOOST_V3_UI_ENABLED } from "./review-boost-v3-ui";
import { REVIEWBOOST_V4_UI_ENABLED } from "./review-boost-v4-ui";
import { OverviewV3View } from "./reputation/views/OverviewV3View";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { PlatformApiClient, PlatformApiError } from "./api";
import type { OrganizationContext } from "./types";
import { StatusState } from "./adaptive/primitives";
import type {
  OverviewData,
  ReviewItem,
  AiAgentSettingsData,
  AiAgentContextProfile,
  AiAgentInstruction,
  AiVoiceLearningData,
  ReputationProgress,
  ReputationSettingsData,
  GoogleStatus,
  GoogleAccount,
  GoogleLocation,
} from "./reputation/types";
import { OverviewView } from "./reputation/views/OverviewView";
import type { ReviewBoostSetupReadiness } from "./reputation/views/ReviewBoostSetupGuide";
import { verifiedGoogleLocationReadback } from "./reputation/google-location-readback";
import { AcquisitionPreviewView } from "./reputation/views/AcquisitionPreviewView";
import { ReviewsView } from "./reputation/views/ReviewsView";
import { AiAgentView } from "./reputation/views/AiAgentView";
import { ReportsView } from "./reputation/views/ReportsView";
import { SettingsView } from "./reputation/views/SettingsView";

export type ReputationView =
  | { kind: "overview" }
  | { kind: "reviews" }
  | { kind: "acquisition" }
  | { kind: "review"; id: string }
  | { kind: "ai-agent" }
  | { kind: "reports"; topic?: string; reportId?: string }
  | { kind: "settings"; section?: string }
  | { kind: "not-found" };

export function reputationViewFor(nestedPath: string): ReputationView {
  const path = nestedPath === "" || nestedPath === "/" ? "/overview" : nestedPath;
  if (path === "/overview" || path === "/home") return { kind: "overview" };
  if (path === "/reviews") return { kind: "reviews" };
  if (path === "/acquisition") return { kind: "acquisition" };
  const review = path.match(/^\/reviews\/([^/]+)$/);
  if (review) return { kind: "review", id: decodeURIComponent(review[1]!) };
  if (path === "/ai-agent") return { kind: "ai-agent" };
  if (path === "/reports") return { kind: "reports" };
  const reportTopic = path.match(/^\/reports\/(?:tema|topics)\/([^/]+)$/);
  if (reportTopic) return { kind: "reports", topic: decodeURIComponent(reportTopic[1]!) };
  const singleReport = path.match(/^\/reports\/([^/]+)$/);
  if (singleReport) return { kind: "reports", reportId: decodeURIComponent(singleReport[1]!) };
  if (path === "/settings") return { kind: "settings" };
  const settingsSection = path.match(/^\/settings\/([^/]+)$/);
  if (settingsSection) return { kind: "settings", section: decodeURIComponent(settingsSection[1]!) };
  return { kind: "not-found" };
}

interface LoadedData {
  overview?: OverviewData;
  reviews?: ReviewItem[];
  aiAgentSettings?: AiAgentSettingsData;
  aiAgentContextProfile?: AiAgentContextProfile;
  aiAgentInstructions?: AiAgentInstruction[];
  aiVoiceLearning?: AiVoiceLearningData;
  progress?: ReputationProgress;
  settings?: ReputationSettingsData;
  googleStatus?: GoogleStatus;
  googleAccounts?: GoogleAccount[];
  googleLocations?: GoogleLocation[];
  googleStatusVerified?: boolean;
  googleAccountsVerified?: boolean;
  googleLocationsVerified?: boolean;
  googleReadiness?: ReviewBoostSetupReadiness;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: LoadedData }
  | { kind: "forbidden" }
  | { kind: "unavailable"; requestId?: string }
  | { kind: "not-found" };

export function ReputationProduct({
  organization,
  nestedPath,
  api,
  navigate,
  onExpired,
  user,
}: {
  organization: OrganizationContext;
  nestedPath: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  onExpired: () => void;
  user?: { id: string; email: string; displayName: string; status: string };
}) {
  const view = useMemo(() => reputationViewFor(nestedPath), [nestedPath]);
  // List and selected review are one workspace: avoid unmounting its search,
  // sorting and unsaved-draft guards during SPA detail/back navigation.
  const loadScope = view.kind === "review" ? "reviews" : view.kind;
  const onExpiredRef = useRef(onExpired);
  // App currently recreates this callback when its URL route changes; keep the
  // latest handler without turning every review selection into a data reload.
  useEffect(() => { onExpiredRef.current = onExpired; }, [onExpired]);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [revision, setRevision] = useState(0);

  const slug = organization.organization.slug;
  const base = `/o/${slug}/reputation`;

  const reload = useCallback(() => {
    setRevision((v) => v + 1);
  }, []);

  useEffect(() => {
    let active = true;

    if (loadScope === "not-found") {
      setState({ kind: "not-found" });
      return () => {
        active = false;
      };
    }

    setState({ kind: "loading" });

    async function load() {
      try {
        const loaded: LoadedData = {};

        if (view.kind === "overview") {
          const [overview, reviewsRes, aiAgentSettingsRes] = await Promise.all([
            api.reputation<OverviewData>(slug, "/overview"),
            api.reputation<{ reviews: ReviewItem[] }>(slug, "/reviews"),
            api.reputation<{ settings: AiAgentSettingsData }>(slug, "/ai-agent/settings").catch(() => undefined),
          ]);
          loaded.overview = overview;
          loaded.reviews = reviewsRes.reviews || [];
          loaded.aiAgentSettings = aiAgentSettingsRes?.settings;

          // UXG-01: derive first-run completion only from read-only, tenant-scoped
          // provider calls. A failed location read is UNKNOWN, never "zero locations".
          const readiness: ReviewBoostSetupReadiness = {
            connectionVerified: false,
            connection: null,
            locationsVerified: false,
            locations: [],
          };
          try {
            const currentGoogle = await api.reputation<GoogleStatus>(slug, "/google/status");
            if (currentGoogle && [
              "connected", "disconnected", "connecting", "expired", "needs_reauth", "error",
            ].includes(currentGoogle.status)) {
              readiness.connection = currentGoogle;
              readiness.connectionVerified = true;
            }
          } catch (cause) {
            if (cause instanceof PlatformApiError && cause.status === 401) throw cause;
            // 403, 404 and transport errors are inconclusive, not disconnected.
          }
          if (readiness.connection?.status === "connected") {
            try {
              const locationResult = await api.reputation<{ locations: GoogleLocation[] }>(
                slug, "/google/locations",
              );
              if (verifiedGoogleLocationReadback(locationResult.locations)) {
                readiness.locations = locationResult.locations;
                readiness.locationsVerified = true;
              }
            } catch (cause) {
              if (cause instanceof PlatformApiError && cause.status === 401) throw cause;
              // Do not mistake a failed readback for an empty provider account.
            }
          }
          loaded.googleReadiness = readiness;
        } else if (loadScope === "reviews") {
          const [reviewsRes, overview] = await Promise.all([
            api.reputation<{ reviews: ReviewItem[] }>(slug, "/reviews"),
            api.reputation<OverviewData>(slug, "/overview").catch(() => undefined),
          ]);
          loaded.reviews = reviewsRes.reviews || [];
          loaded.overview = overview;
        } else if (view.kind === "ai-agent") {
          const [settingsRes, profileRes, instructionsRes, voiceLearningRes, reviewsRes] = await Promise.all([
            api.reputation<{ settings: AiAgentSettingsData; limits?: Record<string, number> }>(slug, "/ai-agent/settings"),
            api.reputation<{ profile: AiAgentContextProfile; catalog?: unknown }>(slug, "/ai-agent/context-profile"),
            api.reputation<{ instructions: AiAgentInstruction[] }>(slug, "/ai-agent/instructions"),
            api.reputation<AiVoiceLearningData>(slug, "/ai-agent/voice-learning").catch(() => undefined),
            api.reputation<{ reviews: ReviewItem[] }>(slug, "/reviews"),
          ]);
          loaded.aiAgentSettings = { ...settingsRes.settings, limits: settingsRes.limits };
          loaded.aiAgentContextProfile = profileRes.profile;
          loaded.aiAgentInstructions = instructionsRes.instructions || [];
          loaded.aiVoiceLearning = voiceLearningRes;
          loaded.reviews = reviewsRes.reviews || [];
        } else if (view.kind === "reports") {
          const progressRes = await api
            .reputation<{ progress: ReputationProgress }>(slug, "/reputation-progress")
            .catch((cause) => {
              if (cause instanceof PlatformApiError && cause.status === 404) return undefined;
              throw cause;
            });
          loaded.progress = progressRes?.progress;
        } else if (view.kind === "settings") {
          const [settingsRes, overview, googleStatus] = await Promise.all([
            api.reputation<{ settings: ReputationSettingsData }>(slug, "/settings"),
            api.reputation<OverviewData>(slug, "/overview").catch(() => undefined),
            api.reputation<GoogleStatus>(slug, "/google/status").catch((cause) => {
              // Expired Platform sessions must not look like a disconnected Google account.
              if (cause instanceof PlatformApiError && cause.status === 401) throw cause;
              return undefined;
            }),
          ]);
          loaded.settings = settingsRes.settings;
          loaded.overview = overview;
          loaded.googleStatusVerified = googleStatus != null && [
            "connected", "disconnected", "connecting", "expired", "needs_reauth", "error",
          ].includes(googleStatus.status);
          loaded.googleStatus = loaded.googleStatusVerified ? googleStatus : undefined;

          if (REVIEWBOOST_V4_UI_ENABLED) {
            try {
              // Reuse the agent's canonical read contracts. A failed automation
              // read must not hide the independent Google/business settings.
              const [agentSettings, profile, instructions, voice, reviews] = await Promise.all([
                api.reputation<{ settings: AiAgentSettingsData; limits?: Record<string, number> }>(slug, "/ai-agent/settings"),
                api.reputation<{ profile: AiAgentContextProfile }>(slug, "/ai-agent/context-profile"),
                api.reputation<{ instructions: AiAgentInstruction[] }>(slug, "/ai-agent/instructions"),
                api.reputation<AiVoiceLearningData>(slug, "/ai-agent/voice-learning").catch(() => undefined),
                api.reputation<{ reviews: ReviewItem[] }>(slug, "/reviews"),
              ]);
              loaded.aiAgentSettings = { ...agentSettings.settings, limits: agentSettings.limits };
              loaded.aiAgentContextProfile = profile.profile;
              loaded.aiAgentInstructions = instructions.instructions || [];
              loaded.aiVoiceLearning = voice;
              loaded.reviews = reviews.reviews || [];
            } catch (cause) {
              if (cause instanceof PlatformApiError && cause.status === 401) throw cause;
              // No defaults: absent evidence remains visibly unavailable.
            }
          }

          if (loaded.googleStatus?.status === "connected") {
            const inconclusive = (cause: unknown) => {
              if (cause instanceof PlatformApiError && cause.status === 401) throw cause;
              // Other provider errors mean unknown, NEVER an empty Google account.
              return undefined;
            };
            const [accounts, locations] = await Promise.all([
              api.reputation<{ accounts: GoogleAccount[] }>(slug, "/google/accounts").catch(inconclusive),
              api.reputation<{ locations: GoogleLocation[] }>(slug, "/google/locations").catch(inconclusive),
            ]);
            if (accounts && Array.isArray(accounts.accounts)) {
              loaded.googleAccounts = accounts.accounts;
              loaded.googleAccountsVerified = true;
            }
            if (locations && Array.isArray(locations.locations)) {
              loaded.googleLocations = locations.locations;
              loaded.googleLocationsVerified = true;
            }
          }
        }

        if (active) {
          setState({ kind: "ready", data: loaded });
        }
      } catch (cause) {
        if (!active) return;
        if (cause instanceof PlatformApiError && cause.status === 401) {
          onExpiredRef.current();
          return;
        }
        if (cause instanceof PlatformApiError && cause.status === 403) {
          setState({ kind: "forbidden" });
        } else if (cause instanceof PlatformApiError && cause.status === 404) {
          setState({ kind: "not-found" });
        } else {
          setState({
            kind: "unavailable",
            requestId: cause instanceof PlatformApiError ? cause.requestId : undefined,
          });
        }
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [api, slug, loadScope, revision]);

  if (state.kind === "loading") {
    return <StatusState
      className="product-boundary available"
      tone="loading"
      eyebrow="Review Boost"
      title="Abriendo Review Boost…"
      message="Cargando información operativa y estado de tu negocio."
    />;
  }

  if (state.kind === "forbidden") {
    return <StatusState
      className="product-boundary unavailable"
      tone="danger"
      live="assertive"
      eyebrow="Acceso restringido"
      title="Permisos insuficientes"
      message="Tu sesión de ClickDialog está activa, pero no contás con permisos para acceder a Review Boost en esta organización."
    />;
  }

  if (state.kind === "unavailable") {
    return <StatusState
      className="product-boundary suspended"
      tone="warning"
      live="assertive"
      eyebrow="Servicio temporalmente no disponible"
      title="Review Boost no está disponible en este momento"
      message="Hubo un inconveniente al conectar con Review Boost. Por favor, intentá nuevamente en unos instantes."
      meta={state.requestId ? `ID de seguimiento: ${state.requestId}` : undefined}
    />;
  }

  if (state.kind === "not-found") {
    return <StatusState
      className="product-boundary unavailable"
      tone="warning"
      eyebrow="Ruta no encontrada"
      title="Sección no disponible"
      message="La sección o recurso solicitado no existe dentro de Review Boost."
      action={<button type="button" className="primary-button" onClick={() => navigate(`${base}/overview`)}>Ir a Inicio de Review Boost</button>}
    />;
  }

  const { data } = state;
  const isGoogleConnected = data.googleStatus
    ? data.googleStatus.status === "connected"
    : data.overview?.google?.status === "connected";
  const greetingName = user?.displayName ? user.displayName.trim().split(/\s+/)[0] : "";
  return (
    <div className="rep-product-container">
      <div className="rep-view-body">
        {view.kind === "acquisition" && <AcquisitionPreviewView slug={slug} api={api} onExpired={onExpired} />}

        {view.kind === "overview" && data.overview && (
          REVIEWBOOST_V3_UI_ENABLED ? <OverviewV3View
            data={data.overview}
            reviews={data.reviews || []}
            aiAgentSettings={data.aiAgentSettings}
            googleReadiness={data.googleReadiness}
            base={base}
            navigate={navigate}
            slug={slug}
            api={api}
            onExpired={onExpired}
            reload={reload}
          /> : <OverviewView
            data={data.overview}
            reviews={data.reviews || []}
            aiAgentSettings={data.aiAgentSettings}
            googleReadiness={data.googleReadiness}
            userName={greetingName || ""}
            base={base}
            navigate={navigate}
          />
        )}

        {(view.kind === "reviews" || view.kind === "review") && (
          <ReviewsView
            reviews={data.reviews || []}
            selectedId={view.kind === "review" ? view.id : null}
            slug={slug}
            api={api}
            base={base}
            navigate={navigate}
            reload={reload}
            googleConnected={isGoogleConnected}
            googleStatus={data.googleStatus?.status ?? data.overview?.google?.status ?? null}
          />
        )}

        {view.kind === "ai-agent" && data.aiAgentSettings && data.aiAgentContextProfile && (
          <AiAgentView
            settings={data.aiAgentSettings}
            contextProfile={data.aiAgentContextProfile}
            instructions={data.aiAgentInstructions || []}
            voiceLearning={data.aiVoiceLearning}
            reviews={data.reviews || []}
            slug={slug}
            api={api}
            reload={reload}
          />
        )}

        {view.kind === "reports" && (
          <ReportsView
            initialData={data.progress || null}
            initialTopic={view.topic || null}
            onExpired={onExpired}
            slug={slug}
            api={api}
            base={base}
            navigate={navigate}
          />
        )}

        {view.kind === "settings" && data.settings && (
          <SettingsView
            settings={data.settings}
            location={data.overview?.locations[0] || null}
            internalLocations={data.overview?.locations || []}
            googleStatus={data.googleStatus ?? null}
            googleStatusVerified={data.googleStatusVerified === true}
            googleAccounts={data.googleAccounts || []}
            googleAccountsVerified={data.googleAccountsVerified === true}
            googleLocations={data.googleLocations || []}
            googleLocationsVerified={data.googleLocationsVerified === true}
            organization={organization}
            initialSection={view.section || (REVIEWBOOST_V4_UI_ENABLED ? "automation" : REVIEWBOOST_V3_UI_ENABLED ? "google" : "negocio")}
            automation={REVIEWBOOST_V4_UI_ENABLED ? data.aiAgentSettings && data.aiAgentContextProfile
              ? <AiAgentView embedded settings={data.aiAgentSettings}
                  contextProfile={data.aiAgentContextProfile}
                  instructions={data.aiAgentInstructions || []}
                  voiceLearning={data.aiVoiceLearning} reviews={data.reviews || []}
                  slug={slug} api={api} reload={reload} />
              : <section role="status"><h2>Automatización sin verificar</h2>
                  <p>No pudimos consultar su configuración. Podés seguir revisando Google, tu negocio y las notificaciones.</p>
                  <button type="button" className="secondary-button" onClick={reload}>Volver a consultar</button>
                </section>
              : undefined}
            slug={slug}
            api={api}
            base={base}
            navigate={navigate}
            reload={reload}
          />
        )}
      </div>
    </div>
  );
}
